/**
 * A spoken Aria session as an ordered list of turns.
 *
 * The Live API streams transcription in fragments, with no turn boundaries of its own. Accumulating
 * those fragments into one string per speaker produces a single wall of text with no ordering — which
 * is what the subtitles showed and what got persisted as the conversation transcript. These helpers
 * cut the stream into turns at every speaker change instead, so the transcript reads (and saves) as a
 * conversation.
 */

import { AssistantSpeakerRole } from '../types/AppAssistant'
import type { CoachVoiceTurn } from '../types/CoachApi'
import { CoachMessageRole } from '../types/CoachTypes'

/** How a speaker is stored in a conversation Aria keeps, from the coach page or the floating assistant. */
export const COACH_ROLE_BY_SPEAKER: Record<AssistantSpeakerRole, CoachMessageRole> = {
  [AssistantSpeakerRole.USER]: CoachMessageRole.USER,
  [AssistantSpeakerRole.ASSISTANT]: CoachMessageRole.ASSISTANT
}

/** Said lines — spoken turns or chat bubbles — as the turns a kept conversation stores. */
export function toCoachVoiceTurns(lines: { role: AssistantSpeakerRole; text: string }[]): CoachVoiceTurn[] {
  return lines.map((line) => ({ role: COACH_ROLE_BY_SPEAKER[line.role], content: line.text.trim() }))
}

export type AriaTranscriptTurn = {
  /** Stable across appends, so React can key on it. */
  id: string
  role: AssistantSpeakerRole
  text: string
  /** False only for the turn currently being spoken — drives the "still talking" indicator. */
  isComplete: boolean
}

/**
 * Placeholders the speech model emits for sounds that are not words. Not cosmetic: spoken turns are
 * persisted and read back to Aria as the history of the conversation, so a `<noise>` left in becomes
 * something she can quote and react to.
 */
const NON_SPEECH_TOKEN = /<\s*(?:noise|silence|unk|inaudible|music|laugh(?:ter)?|breath|cough)\s*>/gi

export function stripNonSpeechTokens(text: string): string {
  return text.replace(NON_SPEECH_TOKEN, '')
}

/**
 * Append a transcription fragment, opening a new turn whenever the speaker changes.
 *
 * Only the last turn is ever open. Without that rule a barge-in (the user starting to talk while Aria
 * is still speaking) would reopen the earlier turn and write the new words above the ones that came
 * before them.
 */
export function appendAriaTranscriptChunk(
  turns: AriaTranscriptTurn[],
  role: AssistantSpeakerRole,
  rawChunk: string
): AriaTranscriptTurn[] {
  // Filtered where every fragment passes through, so nothing downstream — the captions, the saved
  // transcript, the prompt context Aria reads back — ever has to know these placeholders exist.
  const chunk = stripNonSpeechTokens(rawChunk)
  if (!chunk) return turns

  const last = turns[turns.length - 1]
  if (last && last.role === role && !last.isComplete) {
    return [...turns.slice(0, -1), { ...last, text: last.text + chunk }]
  }

  // Whitespace is padding between words, not somebody taking the floor. Letting it open a turn split a
  // sentence across two bubbles, with the empty turn between them hidden by the display filter.
  if (!chunk.trim()) {
    return last ? [...turns.slice(0, -1), { ...last, text: last.text + chunk }] : turns
  }

  const opened: AriaTranscriptTurn = {
    id: `${role}-${turns.length}`,
    role,
    text: chunk,
    isComplete: false
  }
  return last ? [...turns.slice(0, -1), { ...last, isComplete: true }, opened] : [opened]
}

/** Close the open turn, whoever it belongs to — the session is over or the medium is changing. */
export function completeAriaTranscriptTurns(turns: AriaTranscriptTurn[]): AriaTranscriptTurn[] {
  const last = turns[turns.length - 1]
  if (!last || last.isComplete) return turns
  return [...turns.slice(0, -1), { ...last, isComplete: true }]
}

/**
 * Close the open turn only if it belongs to `role`. `turnComplete` ends the MODEL's turn; closing
 * whatever was open ended the user's too, and their lagging transcription then opened a second bubble.
 */
export function completeAriaTranscriptTurnsFor(
  turns: AriaTranscriptTurn[],
  role: AssistantSpeakerRole
): AriaTranscriptTurn[] {
  const last = turns[turns.length - 1]
  if (!last || last.isComplete || last.role !== role) return turns
  return [...turns.slice(0, -1), { ...last, isComplete: true }]
}

/** Prefix so a saved message's id can never collide with a live turn's generated one. */
const SAVED_TURN_ID_PREFIX = 'saved-'

/**
 * Messages already saved in this conversation, as finished turns.
 *
 * Captions show the CONVERSATION, not the current socket. A spoken session starts its transcript from
 * empty every time it connects, so moving between voice, voice-with-subtitles and text used to blank the
 * subtitles even though the typed view still had every word — one conversation, two different answers
 * depending on which button you had pressed last.
 */
export function toSavedAriaTranscriptTurns(
  messages: { id: string; role: AssistantSpeakerRole; text: string }[]
): AriaTranscriptTurn[] {
  return messages
    .filter((message) => message.text.trim().length > 0)
    .map((message) => ({
      id: `${SAVED_TURN_ID_PREFIX}${message.id}`,
      role: message.role,
      text: message.text,
      isComplete: true
    }))
}

/** Turns worth keeping: whitespace-only fragments are transcription noise, not something anyone said. */
export function meaningfulAriaTranscriptTurns(turns: AriaTranscriptTurn[]): AriaTranscriptTurn[] {
  return turns.filter((turn) => turn.text.trim().length > 0)
}

/**
 * What Aria is saying, or last said — the only thing the captions show.
 *
 * Subtitles here are an accessibility aid for someone who cannot hear her, not a transcript: the user's
 * own words are never captioned back at them, and nothing older than her current line is kept on screen.
 */
export function getLatestAriaSpokenText(turns: AriaTranscriptTurn[]): string | undefined {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index]
    if (turn.role === AssistantSpeakerRole.ASSISTANT && turn.text.trim()) return turn.text.trim()
  }
  return undefined
}

/**
 * Who is talking right now, or undefined when nobody is. The open turn is the live one, so this is
 * what lets the UI say "Aria is speaking" / "I can hear you" instead of a single static label.
 */
export function getAriaTranscriptSpeaker(turns: AriaTranscriptTurn[]): AssistantSpeakerRole | undefined {
  const last = turns[turns.length - 1]
  return last && !last.isComplete ? last.role : undefined
}
